import React, { SyntheticEvent, useCallback, useState } from 'react';
import classnames from 'classnames';
import { useHideOnClickOutside } from 'hooks/useHideOnClickOutside';

export interface ITextInputProps {
  type?: 'text' | 'textarea';
  fieldId: string;
  value?: string;
  onChange: (value?: string) => void;
  className?: string;
  inputClassName?: string;
  errorMessage?: string | null;
  errorClassName?: string;
  disabled?: boolean;
  placeholder?: string;
  onKeyUp?: (event) => void;
}

export const TextInput: React.FC<ITextInputProps> = React.memo(
  ({
    type = 'text',
    fieldId,
    value,
    onChange,
    className = '',
    inputClassName = '',
    errorMessage,
    errorClassName,
    disabled = false,
    placeholder,
    onKeyUp,
  }) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [height, setHeight] = useState('auto');
    const inputClassname = classnames('w-full text-[15px] leading-[19px] py-[7px] px-[10px] overflow-hidden text-black outline-none',
      inputClassName,
      {
        'border-gray-40 bg-white-prime': !errorMessage && !disabled,
        'border-red-95': !!errorMessage && !disabled,
        'text-black border border-solid': !disabled,
        'text-gray-100 border-none bg-gray-10': disabled,
        'border-green-prime border-2': isOpen,
      });

    const { instanceClassname } = useHideOnClickOutside(setIsOpen, 'select');

    const openDropdown = useCallback(() => {
      if (!disabled) setIsOpen(true);
    }, [disabled]);

    const handleInputChange = useCallback(
      e => {
        onChange(e.target.value);
      },
      [onChange],
    );

    const handleInput = (event: SyntheticEvent): void => {
      handleInputChange(event);
      const textarea = event.target as HTMLInputElement;
      textarea.style.height = '';
      textarea.style.height = textarea.scrollHeight + 3 + 'px';
      setHeight(textarea.style.height);
    };


    return (
      <div className={classnames(instanceClassname, 'flex flex-col min-w-[75px]', className)}>
        <div
          className={classnames(
            'relative flex w-full cursor-pointer items-center justify-between text-[15px]',
          )}
        >
          <div className="options flex items-center justify-start relative h-full w-full">
            {type === 'textarea' ? (
              <textarea
                className={inputClassname}
                id={fieldId}
                onChange={handleInput}
                value={value}
                autoComplete="off"
                disabled={disabled}
                onFocus={openDropdown}
                style={{ 'resize': 'none', height: height }}
                placeholder={placeholder}
                onKeyUp={onKeyUp}
              />
            ) : (
              <input
                className={inputClassname}
                id={fieldId}
                onChange={handleInputChange}
                value={value}
                autoComplete="off"
                disabled={disabled}
                onFocus={openDropdown}
                placeholder={placeholder}
                onKeyUp={onKeyUp}
              />
            )}
          </div>
        </div>

        {errorMessage && (
          <p className={`error-message text-red-95 text-[13px] leading-2xs font-pt-sans uppercase mt-[5px] ${errorClassName ?? ''}`}>
            {errorMessage}
          </p>
        )}
      </div>
    );
  },
);
